import { apiClient, ApiResponse } from './api'

// Campaign and Product types
export interface Campaign {
  id: number
  name: string
  slug: string
  description?: string
  is_active: boolean
  switchable_campaigns?: Campaign[]
  created_at: string
  updated_at: string
}

export interface ProductVariant {
  id: number
  name: string
  sku?: string
  price: number
  weight?: number
  dimensions?: {
    length: number
    width: number
    height: number
  }
  inventory_quantity?: number
  is_available: boolean
}

export interface CampaignProduct {
  id: number
  campaign_id: number
  name: string
  slug: string
  description?: string
  price: number
  images?: string[]
  variants?: ProductVariant[]
  category?: string
  tags?: string[]
  is_available: boolean
  inventory_quantity?: number
  weight?: number
  dimensions?: {
    length: number
    width: number
    height: number
  }
  bundled_variants?: Array<{
    variant: ProductVariant
    quantity: number
  }>
  created_at: string
  updated_at: string
}

export interface CartItem {
  campaign_product: CampaignProduct
  variant?: ProductVariant
  quantity: number
  bundled_variants?: Array<{
    variant: ProductVariant
    quantity: number
  }>
  buy_now_clicked?: boolean
}

export interface SessionCart {
  items: CartItem[]
  gateway?: string
  subtotal?: number
  tax?: number
  shipping?: number
  total?: number
}

// Campaign and product service functions
export const campaignService = {
  // Get switchable campaigns for session
  async getSwitchableCampaigns(sessionId: number): Promise<Campaign[]> {
    const response = await apiClient.get<ApiResponse<Campaign[]>>(`/sessions/${sessionId}/campaigns/`)
    return response.data.data
  },

  // Get campaign by ID
  async getCampaign(campaignId: number): Promise<Campaign> {
    const response = await apiClient.get<ApiResponse<Campaign>>(`/campaigns/${campaignId}/`)
    return response.data.data
  },

  // Get campaign products
  async getCampaignProducts(
    campaignId: number,
    params?: {
      category?: string
      search?: string
      limit?: number
      offset?: number
    }
  ): Promise<CampaignProduct[]> {
    const response = await apiClient.get<ApiResponse<CampaignProduct[]>>(`/campaigns/${campaignId}/products/`, {
      params,
    })
    return response.data.data
  },

  // Switch to different campaign
  async switchCampaign(sessionId: number, campaignId: number): Promise<Campaign> {
    const response = await apiClient.post<ApiResponse<Campaign>>(`/sessions/${sessionId}/switch_campaign/`, {
      campaign_id: campaignId,
    })
    return response.data.data
  },
}

export const productService = {
  // Get campaign product details
  async getCampaignProduct(productId: number): Promise<CampaignProduct> {
    const response = await apiClient.get<ApiResponse<CampaignProduct>>(`/campaign_products/${productId}/`)
    return response.data.data
  },

  // Get product variants
  async getProductVariants(productId: number): Promise<ProductVariant[]> {
    const response = await apiClient.get<ApiResponse<ProductVariant[]>>(`/campaign_products/${productId}/variants/`)
    return response.data.data
  },

  // Check product availability
  async checkProductAvailability(
    productId: number,
    variantId?: number
  ): Promise<{
    is_available: boolean
    inventory_quantity?: number
    max_quantity?: number
  }> {
    const params: Record<string, any> = {}
    if (variantId) {
      params.variant_id = variantId
    }

    const response = await apiClient.get(`/campaign_products/${productId}/availability/`, { params })
    return response.data.data
  },

  // Search products
  async searchProducts(
    query: string,
    filters?: {
      campaign_id?: number
      category?: string
      min_price?: number
      max_price?: number
      limit?: number
    }
  ): Promise<CampaignProduct[]> {
    const params = { q: query, ...filters }
    const response = await apiClient.get<ApiResponse<CampaignProduct[]>>('/campaign_products/search/', { params })
    return response.data.data
  },
}

export const sessionCartService = {
  // Get session cart
  async getSessionCart(sessionId: number): Promise<SessionCart> {
    const response = await apiClient.get(`/sessions/${sessionId}/cart/`)
    return response.data?.data || response.data
  },

  // Update session cart
  async updateSessionCart(
    sessionId: number,
    cartData: {
      items: Array<{
        campaign_product: number
        variant?: number | null
        quantity: number
        bundled_variants?: Array<{
          variant: number
          quantity: number
        }> | null
        buy_now_clicked?: boolean
      }>
      customer?: Record<string, any>
    }
  ): Promise<SessionCart> {
    const response = await apiClient.post(`/sessions/${sessionId}/cart/`, cartData)
    // API returns cart data directly (not wrapped in ApiResponse)
    const data = response.data?.data || response.data
    return data || ({} as SessionCart)
  },

  // Add items to session cart
  async addToSessionCart(
    sessionId: number,
    items: Array<{
      campaign_product: CampaignProduct
      variant?: ProductVariant
      quantity: number
      bundled_variants?: Array<{
        variant: ProductVariant
        quantity: number
      }>
      buy_now_clicked?: boolean
    }>
  ): Promise<SessionCart> {
    // Convert items to API format — must include null for variant/bundled_variants (backend expects them)
    const apiItems = items.map(item => ({
      campaign_product: item.campaign_product.id,
      variant: item.variant?.id ?? null,
      quantity: item.quantity,
      bundled_variants: item.bundled_variants
        ? item.bundled_variants.map(bundle => ({
            variant: bundle.variant.id,
            quantity: bundle.quantity,
          }))
        : null,
      buy_now_clicked: item.buy_now_clicked || false,
    }))

    return this.updateSessionCart(sessionId, { items: apiItems, customer: {} })
  },

  // Clear session cart
  async clearSessionCart(sessionId: number): Promise<SessionCart> {
    return this.updateSessionCart(sessionId, { items: [] })
  },

  // Update cart item quantity
  async updateCartItemQuantity(
    sessionId: number,
    productId: number,
    quantity: number,
    variantId?: number
  ): Promise<SessionCart> {
    // First get current cart
    const currentCart = await this.getSessionCart(sessionId)

    // Update the specific item
    const updatedItems = currentCart.items
      .map(item => {
        if (item.campaign_product.id === productId && (!variantId || item.variant?.id === variantId)) {
          return { ...item, quantity }
        }
        return item
      })
      .filter(item => item.quantity > 0) // Remove items with 0 quantity

    // Convert to API format and update
    const apiItems = updatedItems.map(item => ({
      campaign_product: item.campaign_product.id,
      variant: item.variant?.id ?? null,
      quantity: item.quantity,
      bundled_variants: item.bundled_variants
        ? item.bundled_variants.map(bundle => ({
            variant: bundle.variant.id,
            quantity: bundle.quantity,
          }))
        : null,
      buy_now_clicked: item.buy_now_clicked || false,
    }))

    return this.updateSessionCart(sessionId, { items: apiItems })
  },
}
